import { formatPhone } from "../employee/phone";

/**
 * The sign-in alias: the permanent, undeliverable address a person added with
 * ONLY a mobile number is stored under (spec: "Identity: how a phone-only
 * person exists").
 *
 * Every person table keys on email (users, agents, role assignments, WLM
 * parties and details, UAM), so rather than make email optional in ~80 places,
 * a mobile-only person gets an address in the reserved `.invalid` TLD (RFC
 * 2606/6761): it can never route or receive mail and can never collide with a
 * real one. It is minted once from a random id — never from the number — so
 * changing their number never re-keys anything.
 *
 * Three things must know about it: mail (dropped at sendEmail), suite-app
 * provisioning (skipped: "needs an email"), and display (the phone is shown
 * instead). Nothing else may hard-code the domain.
 */
export const SIGN_IN_ALIAS_DOMAIN = "sign-in.skysupport.invalid";

/**
 * Why a phone-only person's number can't be removed: it's how they sign in,
 * and with only a sign-in alias for an email they'd be locked out.
 */
export const ONLY_SIGN_IN_NUMBER =
  "This number is their only way to sign in, so it can't be removed. To stop them signing in, remove their access instead.";

export function mintSignInAlias(): string {
  return `u-${globalThis.crypto.randomUUID()}@${SIGN_IN_ALIAS_DOMAIN}`;
}

export function isSignInAlias(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.trim().toLowerCase().endsWith(`@${SIGN_IN_ALIAS_DOMAIN}`);
}

/** The address to show for a person: their email, or — for an alias — their phone, else "". */
export function displayIdentity(p: {
  email: string | null | undefined;
  phone?: string | null;
}): string {
  if (p.email && !isSignInAlias(p.email)) return p.email.trim();
  return p.phone ? formatPhone(p.phone) : "";
}

/**
 * An email to show as text, or "" for a sign-in alias: where a person is shown
 * without their phone, a phone-only person shows no address rather than the
 * alias. Never use it to identify anyone: it's for display only.
 */
export function shownEmail(email: string | null | undefined): string {
  if (!email || isSignInAlias(email)) return "";
  return email.trim();
}

/** "Name (email)" or "Name — email" for a label; just the name for a sign-in alias. */
export function labelWithEmail(
  name: string,
  email: string | null | undefined,
  style: "brackets" | "dash" = "brackets",
): string {
  const shown = shownEmail(email);
  if (!shown) return name;
  return style === "dash" ? `${name} — ${shown}` : `${name} (${shown})`;
}
